/**
 * PushHandoffPrompt — confirmation step when an operator clicks "Hand off"
 * in Push Mode. Forces an explicit assignee (role OR person) + due date so
 * the resulting follow-up has a real owner and is routable through the
 * centralized handoff resolver (handoffRouting.ts).
 *
 * Pure presentation. State + task creation live in the modal that owns it.
 */
import { useState } from "react";
import { UserPlus, ArrowLeft, Check, Users, User, ChevronDown, ChevronRight } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLES, type UserRole } from "@/data/RoleContext";
import { INTENT_LABELS } from "@/data/handoffActions";
import type { HandoffIntent } from "@/data/opportunityTypes";

export interface PushHandoffSelection {
  /** Display label, always populated for UI. */
  label: string;
  /** Canonical role key when routing by seat. Null when routing direct-to-user. */
  roleKey: UserRole | null;
  /** Optional user id when routing direct-to-user. */
  userId: string | null;
  /** Optional Phase-2 declared intent. */
  intent?: HandoffIntent;
}

export interface PushHandoffPromptProps {
  /** Default assignee label (usually current user's display label). */
  defaultAssignee: string;
  /** Default due date (ISO string). */
  defaultDueDate: string;
  /** Reason fragment shown to the operator for context. */
  reasonFragment: string;
  /** Optional pre-filled intent. When set, the picker opens expanded. */
  defaultIntent?: HandoffIntent;
  onCancel: () => void;
  /**
   * Backward-compatible callback. Existing modals receive (label, dueIso).
   * The full structured selection is passed via `onConfirmStructured` when
   * provided, so canonical fields can be persisted by the engine.
   */
  onConfirm: (assignee: string, dueDateIso: string) => void;
  /** Preferred callback — receives canonical routing data. */
  onConfirmStructured?: (selection: PushHandoffSelection, dueDateIso: string) => void;
}

type Mode = "role" | "person";

const INTENT_OPTIONS: HandoffIntent[] = [
  "general_handoff",
  "install_review",
  "material_confirmation",
  "schedule_coordination",
  "finance_followup",
  "service_resolution",
  "estimate_revision",
  "permit_followup",
  "customer_contact",
];

export function PushHandoffPrompt({
  defaultAssignee, defaultDueDate, reasonFragment, defaultIntent, onCancel, onConfirm, onConfirmStructured,
}: PushHandoffPromptProps) {
  const [mode, setMode] = useState<Mode>("role");
  // Role mode: pick a role key (canonical seat).
  const [roleKey, setRoleKey] = useState<UserRole>("head_of_installation");
  // Person mode: free-text name (no user directory in beta — name doubles as id).
  const [personName, setPersonName] = useState(defaultAssignee);
  const dateOnly = defaultDueDate.slice(0, 10);
  const [due, setDue] = useState(dateOnly);
  // Phase-2: optional intent. Collapsed by default to keep small-op UX clean.
  const [intentOpen, setIntentOpen] = useState<boolean>(!!defaultIntent);
  const [intent, setIntent] = useState<HandoffIntent | "auto">(defaultIntent ?? "auto");

  const submit = () => {
    const d = new Date(`${due}T09:00:00`);
    const dueIso = d.toISOString();

    let selection: PushHandoffSelection;
    if (mode === "role") {
      const roleInfo = ROLES.find((r) => r.key === roleKey);
      selection = {
        label: roleInfo?.label ?? roleKey,
        roleKey,
        userId: null,
        intent: intent === "auto" ? undefined : intent,
      };
    } else {
      const name = personName.trim() || defaultAssignee;
      selection = {
        label: name,
        roleKey: null,
        // Beta: use the trimmed name as a stable userId surrogate. When a user
        // directory ships, swap this for the real id without changing callers.
        userId: name,
        intent: intent === "auto" ? undefined : intent,
      };
    }

    onConfirmStructured?.(selection, dueIso);
    onConfirm(selection.label, dueIso);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 flex items-start gap-3">
        <div className="h-7 w-7 rounded-full bg-warning/15 flex items-center justify-center shrink-0">
          <UserPlus className="h-4 w-4 text-warning" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-foreground">Hand off — create follow-up</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Route to a role (recommended — anyone in that seat will see it) or to a specific person.
            The task appears in their Command Center under Action Required.
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
        <button
          type="button"
          onClick={() => setMode("role")}
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded transition-colors ${
            mode === "role"
              ? "bg-background text-foreground shadow-sm font-semibold"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Users className="h-3 w-3" /> By role
        </button>
        <button
          type="button"
          onClick={() => setMode("person")}
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded transition-colors ${
            mode === "person"
              ? "bg-background text-foreground shadow-sm font-semibold"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <User className="h-3 w-3" /> By person
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ho-assignee" className="text-[11px] font-semibold">
            {mode === "role" ? "Assign to role" : "Assign to person"}
          </Label>
          {mode === "role" ? (
            <Select value={roleKey} onValueChange={(v) => setRoleKey(v as UserRole)}>
              <SelectTrigger id="ho-assignee" className="h-9 text-[12px]">
                <SelectValue placeholder="Pick a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="ho-assignee"
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="Enter name"
              className="h-9 text-[12px]"
            />
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ho-due" className="text-[11px] font-semibold">Due date</Label>
          <Input
            id="ho-due"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="h-9 text-[12px]"
          />
        </div>
      </div>

      {/* Phase-2: optional intent (collapsed by default) */}
      <div className="rounded-md border border-border/60 bg-muted/20">
        <button
          type="button"
          onClick={() => setIntentOpen((v) => !v)}
          className="w-full flex items-center justify-between px-2.5 py-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <span className="inline-flex items-center gap-1.5">
            {intentOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Intent <span className="text-muted-foreground/70">(optional — auto-detected)</span>
          </span>
          {intent !== "auto" && (
            <span className="text-[10px] font-medium text-foreground">{INTENT_LABELS[intent]}</span>
          )}
        </button>
        {intentOpen && (
          <div className="px-2.5 pb-2.5 pt-0.5">
            <Select value={intent} onValueChange={(v) => setIntent(v as HandoffIntent | "auto")}>
              <SelectTrigger className="h-8 text-[11px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                {INTENT_OPTIONS.map((i) => (
                  <SelectItem key={i} value={i}>{INTENT_LABELS[i]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Tells the receiver's inbox what action to surface. Leave on auto-detect for most cases.
            </p>
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground">
        Reason: <span className="font-medium text-foreground">{reasonFragment}</span>
      </p>

      <div className="border-t border-border pt-3 flex items-center justify-between gap-2">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border bg-muted/40 hover:bg-muted text-foreground transition-colors"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <button
          onClick={submit}
          disabled={(mode === "person" && !personName.trim()) || !due}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border border-warning/40 bg-warning/10 text-warning hover:bg-warning/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="h-3 w-3" /> Confirm hand-off
        </button>
      </div>
    </div>
  );
}
